import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Button,
  DialogModal,
  Drawer,
  PanelHeader,
  Text,
} from "@pushpress/shared-components";
import {
  defaultToastOptions,
  extractError,
  joinStrings,
} from "../../../utils/misc";
import { getCurrencySymbol } from "../../../utils/currencyUtils";
import { AppContext } from "../../../context/app";
import { PayRatesValues } from "./AppointmentPayRateList.types";
import PayRatesForm from "./PayRatesForm";
import {
  StaffRowFragment,
  useCreatePayRateMutation,
  useDeletePayRateMutation,
  useUpdatePayRateMutation,
} from "./AppointmentPayRateList-generated.hooks";
import { createPayRatesFormData } from "./AppointmentPayRateList.utils";

interface PayRatesDrawerProps {
  staff?: StaffRowFragment;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function PayRatesDrawer(props: PayRatesDrawerProps) {
  const { visible } = props;

  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="420px"
      disableClickAway
      disableEscapeKeyDown
    >
      <PayRatesDrawerContent {...props} />
    </Drawer>
  );
}

function PayRatesDrawerContent({
  staff,
  onClose,
  onSuccess,
}: PayRatesDrawerProps) {
  const {
    client: { currencyIso },
  } = useContext(AppContext);

  const [saving, setSaving] = useState(false);
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const [createPayRate] = useCreatePayRateMutation();
  const [deletePayRate] = useDeletePayRateMutation();
  const [updatePayRate] = useUpdatePayRateMutation();

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty },
  } = useForm<PayRatesValues>({
    mode: "onTouched",
  });

  useEffect(() => {
    reset(
      createPayRatesFormData({
        ...staff,
        currencySymbol: getCurrencySymbol(currencyIso),
      }),
    );
  }, [staff, currencyIso, reset]);

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      onClose();
    }
  };

  const save = async (values: PayRatesValues) => {
    const staffUuid = staff.userUuid;
    try {
      setSaving(true);

      for (const item of values.types) {
        const appointmentTypeUuid = item.appointmentType.uuid;
        const rateType = item.rateType?.value;
        const value = item.value;

        // create
        if (!item.saved && item.enabled) {
          await createPayRate({
            variables: {
              input: {
                appointmentTypeUuid,
                staffUuid,
                rateType,
                value,
              },
            },
          });
        }

        // delete
        if (item.saved && !item.enabled) {
          await deletePayRate({
            variables: {
              uuid: item.payRateUuid,
            },
          });
        }

        // update
        if (item.saved && item.enabled) {
          await updatePayRate({
            variables: {
              uuid: item.payRateUuid,
              input: {
                rateType,
                value,
              },
            },
          });
        }
      }

      toast.success("Save successful", defaultToastOptions);
      onSuccess(); // onSuccess will refetch the staff with the pay rates
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PanelHeader title={joinStrings(staff?.firstName, staff?.lastName)}>
        <PanelHeader.ActionMenu>
          <Button
            text="Close"
            buttonType="secondary"
            size="medium"
            onClick={handleClose}
          />
          <Button
            text="Save"
            buttonType="primary"
            size="medium"
            type="button"
            loading={saving}
            onClick={handleSubmit(save)}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <div className="p-4">
        <Text variant="heading-4" color="wolf-800" className="mb-1">
          Appointment pay rates
        </Text>
        <Text variant="body-md" color="wolf-600" className="mb-4">
          Define flat amount or % rates for this staff
        </Text>
        <PayRatesForm
          control={control}
          trigger={trigger}
          onSubmit={handleSubmit(save)}
        />
      </div>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
    </>
  );
}

export default PayRatesDrawer;
